import axios from "axios";
import { config } from "@/config";
import { clearAuthToken, loadAuthToken } from "@/utils/auth-token";

/* =====================================================================
   Typed client for the RTO backend (through the ms-taffarel gateway).
   Server-authoritative: the LP renders what these endpoints return —
   prices, collateral math, tenure and scheduling all come from here,
   never from tables hardcoded in the frontend.
   ===================================================================== */

export type Family = "COMFORT" | "CPX";
export type BikeCondition = "NEW" | "USED";
export type PricingModel = "TIER" | "LINEAR" | "UNLIMITED";
export type RtoPath = "KEEP" | "CHANGE";
export type CollateralPlan = "UPFRONT" | "INSTALLMENTS";

export type LpState =
  | "ineligible"
  | "offer"
  | "payment_pending"
  | "signature_pending"
  | "scheduling_pending"
  | "scheduled"
  | "cx_review"
  | "completed";

export interface RiderInfo {
  first_name: string | null;
  masked_email: string | null;
}

export interface StatusResponse {
  state: LpState;
  reason?: string | null;
  path?: RtoPath | null;
  amount_due?: number | null;
  surplus?: number | null;
  payment_plan?: CollateralPlan | null;
  station_id?: number | null;
  pickup_date?: string | null;
  effective_term_months?: number | null;
  rider?: RiderInfo;
}

export interface PriceOption {
  pricing_model: PricingModel;
  price_id: number;
  weekly: number;
  variable_rate: number | null;
  ceiling: number | null;
}

export interface CollateralQuote {
  paid: number;
  upfront: { target: number; due: number };
  installments: {
    target_total: number;
    entry: number;
    entry_due: number;
    weekly_installments: number;
    installment_value: number;
  };
  surplus: number;
}

export interface OfferPath {
  path: RtoPath;
  options: PriceOption[];
  tenure_credit_months: number;
  effective_term_months: number;
  collateral: CollateralQuote;
  discloses_tier_to_linear: boolean;
  discloses_price_jump: boolean;
}

export interface Offer {
  segment: {
    family: Family;
    bike_condition: BikeCondition;
    pricing_model: PricingModel;
  };
  tenure_months: number;
  paths: OfferPath[];
}

export interface EligibilityResponse {
  eligible: boolean;
  reason: string | null;
  rider: RiderInfo | null;
  offer: Offer | null;
}

export type PaymentMethod = "PIX" | "CHECKOUT";

export interface CollateralLinkResponse {
  amount_due: number;
  entry_due: number;
  surplus: number;
  init_point: string | null;
  pix: { qr_code: string; qr_code_base64: string } | null;
  schedule: { weekly_installments: number; installment_value: number } | null;
  state: LpState;
}

export interface ConfirmResponse {
  success: true;
  alreadyMigrated?: boolean;
  effective_term_months?: number;
}

export interface Availability {
  window_start: string | null;
  window_end: string | null;
  stations: {
    station_id: number;
    name: string;
    address: string | null;
    days: { date: string; remaining: number }[];
  }[];
}

export interface ContractStatus {
  signed: boolean;
  signed_document_file_id?: number | null;
  signed_at?: string | null;
}

const rtoBase = (): string => `${config.apiUrl}/v1/rto-migration`;
const contractBase = (): string => `${config.apiUrl}/v1/rto-contract`;

const authHeaders = (): Record<string, string> => {
  const token = loadAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

// Stable backend reason (e.g. "day_fully_booked") for a failed call, or null.
export const errorReason = (error: unknown): string | null => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === "string") return data.message;
  }
  return null;
};

export const errorStatus = (error: unknown): number | null =>
  axios.isAxiosError(error) ? (error.response?.status ?? null) : null;

const handle401 = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    clearAuthToken();
  }
  throw error;
};

export const fetchStatus = async (): Promise<StatusResponse> => {
  try {
    const res = await axios.get<StatusResponse>(`${rtoBase()}/status`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchEligibility = async (): Promise<EligibilityResponse> => {
  try {
    const res = await axios.get<EligibilityResponse>(`${rtoBase()}/eligibility`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postChoice = async (params: {
  path: RtoPath;
  pricingModel: PricingModel;
}): Promise<void> => {
  try {
    await axios.post(
      `${rtoBase()}/choice`,
      { path: params.path, pricing_model: params.pricingModel },
      { headers: authHeaders() },
    );
  } catch (error) {
    return handle401(error);
  }
};

export const postCollateralLink = async (
  plan: CollateralPlan,
  method: PaymentMethod = "CHECKOUT",
  dueDate?: string,
): Promise<CollateralLinkResponse> => {
  try {
    const res = await axios.post<CollateralLinkResponse>(
      `${rtoBase()}/collateral/link`,
      { plan, method, due_date: dueDate },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchCollateralStatus = async (): Promise<{ paid: boolean; state: LpState }> => {
  try {
    const res = await axios.get<{ paid: boolean; state: LpState }>(
      `${rtoBase()}/collateral/status`,
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postConfirm = async (): Promise<ConfirmResponse> => {
  try {
    const res = await axios.post<ConfirmResponse>(
      `${rtoBase()}/confirm`,
      {},
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchAvailability = async (): Promise<Availability> => {
  try {
    const res = await axios.get<Availability>(`${rtoBase()}/scheduling/availability`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postSchedule = async (params: {
  stationId: number;
  date: string;
}): Promise<{ success: true; station_id: number; date: string }> => {
  try {
    const res = await axios.post<{ success: true; station_id: number; date: string }>(
      `${rtoBase()}/schedule`,
      { station_id: params.stationId, date: params.date },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

/* ------------------- contract (leoparda, rider-scoped) ------------------- */

export const fetchContractHtml = async (): Promise<string> => {
  try {
    const res = await axios.get<{ html: string }>(contractBase(), {
      headers: authHeaders(),
    });
    return res.data.html;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchContractStatus = async (): Promise<ContractStatus> => {
  try {
    const res = await axios.get<ContractStatus>(`${contractBase()}/status`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postContractSign = async (
  signatureDataUrl: string,
): Promise<{ success: boolean; already_signed: boolean }> => {
  try {
    const res = await axios.post<{ success: boolean; already_signed: boolean }>(
      `${contractBase()}/sign`,
      { signature: signatureDataUrl },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};
